import React, { useMemo, useState } from "react";
import {
  CBadge,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CFormSelect,
  CRow,
  CTooltip,
} from "@coreui/react";
import type { OptimizationStudyTrial } from "@src/types";
import { CDataTable } from "@tradingdroid/frontends-components";

import { ColoredValue } from "@src/components/ColoredValue";

interface Props {
  trials: OptimizationStudyTrial[];
  bestTrialNumber: number;
}

const PLATEAU_LABEL_COLORS: Record<string, string> = {
  strong_plateau: "success",
  moderate_plateau: "warning",
  weak_plateau: "secondary",
  spiky: "danger",
};

const PLATEAU_LABEL_HELP: Record<string, string> = {
  strong_plateau: "Neighbours perform nearly as well. Most likely to hold up live.",
  moderate_plateau: "Neighbours perform somewhat worse, but the region is still workable.",
  weak_plateau: "The surrounding region is uneven. Treat the result with caution.",
  spiky: "This trial stands well above its neighbours. Usually luck, rarely survives live.",
  insufficient_neighbors: "Too few comparable trials nearby to judge stability.",
  sparse_support: "Nearest comparable trials are too far away to trust the score.",
};

type SortColumn =
  | "default"
  | "cumulative_returns"
  | "win_rate"
  | "profit_factor"
  | "sortino_ratio"
  | "sharpe_ratio"
  | "max_drawdown_pct"
  | "total_positions";

export const ResultsStudyTrialsTable: React.FC<Props> = ({ trials, bestTrialNumber }) => {
  const [sortBy, setSortBy] = useState<SortColumn>("default");

  // Sort trials based on selected column
  const sortedTrials = useMemo(() => {
    const trialsCopy = [...trials];

    if (sortBy === "default") {
      // Default: best trial first, rest by cumulative returns descending
      const sorted = trialsCopy.sort((a, b) => {
        if (a.trial_number === bestTrialNumber) return -1;
        if (b.trial_number === bestTrialNumber) return 1;
        return b.metrics.cumulative_returns - a.metrics.cumulative_returns;
      });
      return sorted;
    }

    // Custom sort by selected column (descending)
    const sorted = trialsCopy.sort((a, b) => {
      const aValue = a.metrics[sortBy];
      const bValue = b.metrics[sortBy];
      return bValue - aValue;
    });
    return sorted;
  }, [trials, bestTrialNumber, sortBy]);

  const trialTableColumns = [
    {
      columnKey: "rank",
      columnLabel: "Rank",
      columnStyle: { width: "60px", textAlign: "right" as const },
      cellRender: (trial: OptimizationStudyTrial) => (
        <ColoredValue value={trial.rank} type="flat" />
      ),
    },
    {
      columnKey: "trial_number",
      columnLabel: "Trial #",
      columnStyle: { width: "80px", textAlign: "right" as const },
      cellRender: (trial: OptimizationStudyTrial) => (
        <ColoredValue value={trial.trial_number} type="flat" />
      ),
    },
    {
      columnKey: "robustness",
      columnLabel: "Robustness",
      columnStyle: { width: "150px" },
      cellRender: (trial: OptimizationStudyTrial) => {
        const plateauRank = trial.metric_rankings?.plateau_score;
        const isBestSharpe =
          trial.robustness?.is_best_sharpe_trial || trial.metric_rankings?.dsr_best_sharpe === 1;
        const label = trial.robustness?.plateau?.label;

        if (plateauRank === undefined && !isBestSharpe) return null;

        return (
          <div className="TrialRobustnessBadges d-flex gap-1 flex-wrap">
            {plateauRank !== undefined && (
              <CTooltip
                content={
                  PLATEAU_LABEL_HELP[label ?? ""] ??
                  "How stable this trial's parameter neighbourhood is. Lower rank is more stable."
                }
                placement="top"
              >
                <CBadge color={PLATEAU_LABEL_COLORS[label ?? ""] ?? "secondary"}>
                  Plateau #{plateauRank}
                </CBadge>
              </CTooltip>
            )}
            {isBestSharpe && (
              <CTooltip
                content="Highest Sharpe in the study. This is the trial the deflated Sharpe was computed on."
                placement="top"
              >
                <CBadge color="info">Best SR</CBadge>
              </CTooltip>
            )}
          </div>
        );
      },
    },
    {
      columnKey: "cumulative_returns",
      columnLabel: "Cumulative Returns",
      columnStyle: { textAlign: "right" as const },
      cellRender: (trial: OptimizationStudyTrial) => (
        <ColoredValue value={trial.metrics.cumulative_returns} threshold={0} />
      ),
    },
    {
      columnKey: "win_rate",
      columnLabel: "Win Rate",
      columnStyle: { textAlign: "right" as const },
      cellRender: (trial: OptimizationStudyTrial) => (
        <ColoredValue value={trial.metrics.win_rate * 100} type="percentage" />
      ),
    },
    {
      columnKey: "profit_factor",
      columnLabel: "Profit Factor",
      columnStyle: { textAlign: "right" as const },
      cellRender: (trial: OptimizationStudyTrial) => (
        <ColoredValue value={trial.metrics.profit_factor} />
      ),
    },
    {
      columnKey: "sortino_ratio",
      columnLabel: "Sortino Ratio",
      columnStyle: { textAlign: "right" as const },
      cellRender: (trial: OptimizationStudyTrial) => (
        <ColoredValue value={trial.metrics.sortino_ratio} />
      ),
    },
    {
      columnKey: "sharpe_ratio",
      columnLabel: "Sharpe Ratio",
      columnStyle: { textAlign: "right" as const },
      cellRender: (trial: OptimizationStudyTrial) => (
        <ColoredValue value={trial.metrics.sharpe_ratio} />
      ),
    },
    {
      columnKey: "max_drawdown_pct",
      columnLabel: "Max Drawdown",
      columnStyle: { textAlign: "right" as const },
      cellRender: (trial: OptimizationStudyTrial) => (
        <ColoredValue value={trial.metrics.max_drawdown_pct} type="percentage" />
      ),
    },
    {
      columnKey: "total_positions",
      columnLabel: "Total Positions",
      columnStyle: { width: "120px", textAlign: "right" as const },
      cellRender: (trial: OptimizationStudyTrial) => (
        <ColoredValue value={trial.metrics.total_positions} type="flat" />
      ),
    },
    {
      columnKey: "win_signals",
      columnLabel: "Win Signals",
      columnStyle: { width: "100px", textAlign: "right" as const },
      cellRender: (trial: OptimizationStudyTrial) => (
        <ColoredValue value={trial.metrics.win_signals} type="flat" />
      ),
    },
    {
      columnKey: "loss_signals",
      columnLabel: "Loss Signals",
      columnStyle: { width: "100px", textAlign: "right" as const },
      cellRender: (trial: OptimizationStudyTrial) => (
        <ColoredValue value={trial.metrics.loss_signals} type="flat" />
      ),
    },
  ];

  return (
    <CCard className="mb-4">
      <CCardHeader className="TrialResultsHeader">
        <CRow className="align-items-center">
          <CCol>
            <h6 className="mb-0">
              Trial Results{" "}
              <span className="text-body-secondary fw-normal fs-sm">
                Showing {sortedTrials.length} selected trials
              </span>
            </h6>
          </CCol>
          <CCol xs="auto">
            <div className="SortTrialsBySelect d-flex align-items-center gap-2">
              <label htmlFor="sortTrialsBy" className="mb-0">
                Sort by:
              </label>
              <CFormSelect
                id="sortTrialsBy"
                value={sortBy}
                size="sm"
                onChange={(e) => setSortBy(e.target.value as SortColumn)}
                style={{ width: "200px" }}
              >
                <option value="default">Default (Best First)</option>
                <option value="cumulative_returns">Cumulative Returns</option>
                <option value="win_rate">Win Rate</option>
                <option value="profit_factor">Profit Factor</option>
                <option value="sortino_ratio">Sortino Ratio</option>
                <option value="sharpe_ratio">Sharpe Ratio</option>
                <option value="max_drawdown_pct">Max Drawdown %</option>
                <option value="total_positions">Total Positions</option>
              </CFormSelect>
            </div>
          </CCol>
        </CRow>
      </CCardHeader>
      <CCardBody className="TrialResultsTableBody p-0">
        <CDataTable
          columns={trialTableColumns}
          items={sortedTrials}
          getRowKey={(trial) => trial.trial_number}
          striped
          small
          hover
          responsive
        />
      </CCardBody>
    </CCard>
  );
};
